

import { Direction, Directionality } from '@angular/cdk/bidi';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  SimpleChanges
} from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { BooleanInput } from 'mwc/core/types';
import { InputBoolean } from 'mwc/core/util';

import { MenuService } from './menu.service';
import { MwcIsMenuInsideDropDownToken } from './menu.token';
import { MwcSubmenuService } from './submenu.service';

@Component({
  selector: '[mwc-menu-item]',
  exportAs: 'mwcMenuItem',
  template: `<ng-content></ng-content> <span mwc-icon mwcIconfont="chevron-right" class="mwc-menu-submenu-arrow"></span>`,
  host: {
    '[class.mwc-dropdown-menu-item]': `isMenuInsideDropDown`,
    '[class.mwc-dropdown-menu-item-selected]': `isMenuInsideDropDown && mwcSelected`,
    '[class.mwc-dropdown-menu-item-danger]': `isMenuInsideDropDown && mwcDanger`,
    '[class.mwc-dropdown-menu-item-disabled]': `isMenuInsideDropDown && mwcDisabled`,
    '[class.mwc-menu-item]': `!isMenuInsideDropDown`,
    '[class.mwc-menu-item-selected]': `!isMenuInsideDropDown && mwcSelected`,
    '[class.mwc-menu-item-danger]': `!isMenuInsideDropDown && mwcDanger`,
    '[class.mwc-menu-item-disabled]': `!isMenuInsideDropDown && mwcDisabled`,
    '[style.paddingLeft.px]': `dir === 'rtl' ? null : mwcPaddingLeft || inlinePaddingLeft`,
    '[style.paddingRight.px]': `dir === 'rtl' ? mwcPaddingLeft || inlinePaddingLeft : null`,
    '(click)': 'clickMenuItem($event)'
  }
})
export class MwcMenuItemDirective implements OnInit, OnChanges, OnDestroy, AfterContentInit {
  static ngAcceptInputType_mwcDisabled: BooleanInput;
  static ngAcceptInputType_mwcSelected: BooleanInput;
  static ngAcceptInputType_mwcDanger: BooleanInput;
  static ngAcceptInputType_mwcMatchRouterExact: BooleanInput;
  static ngAcceptInputType_mwcMatchRouter: BooleanInput;

  private destroy$ = new Subject();
  level = this.mwcSubmenuService ? this.mwcSubmenuService.level + 1 : 1;
  selected$ = new Subject<boolean>();
  inlinePaddingLeft: number | null = null;
  dir: Direction = 'ltr';
  @Input() mwcPaddingLeft?: number;
  @Input() @InputBoolean() mwcDisabled = false;
  @Input() @InputBoolean() mwcSelected = false;
  @Input() @InputBoolean() mwcDanger = false;
  @Input() @InputBoolean() mwcMatchRouterExact = false;
  @Input() @InputBoolean() mwcMatchRouter = false;
  @ContentChildren(RouterLink, { descendants: true }) listOfRouterLink!: QueryList<RouterLink>;

  /** clear all item selected status except this */
  clickMenuItem(e: MouseEvent): void {
    if (this.mwcDisabled) {
      e.preventDefault();
      e.stopPropagation();
    } else {
      this.mwcMenuService.onDescendantMenuItemClick(this);
      if (this.mwcSubmenuService) {
        /** menu item inside the submenu **/
        this.mwcSubmenuService.onChildMenuItemClick(this);
      } else {
        /** menu item inside the root menu **/
        this.mwcMenuService.onChildMenuItemClick(this);
      }
    }
  }

  setSelectedState(value: boolean): void {
    this.mwcSelected = value;
    this.selected$.next(value);
  }

  private updateRouterActive(): void {
    if (!this.listOfRouterLink || !this.router || !this.router.navigated || !this.mwcMatchRouter) {
      return;
    }
    Promise.resolve().then(() => {
      const hasActiveLinks = this.hasActiveLinks();
      if (this.mwcSelected !== hasActiveLinks) {
        this.mwcSelected = hasActiveLinks;
        this.setSelectedState(this.mwcSelected);
        this.cdr.markForCheck();
      }
    });
  }

  private hasActiveLinks(): boolean {
    const isActiveCheckFn = this.isLinkActive(this.router!);
    return (this.routerLink && isActiveCheckFn(this.routerLink)) || this.listOfRouterLink.some(isActiveCheckFn);
  }

  private isLinkActive(router: Router): (link: RouterLink) => boolean {
    return (link: RouterLink) =>
      router.isActive(link.urlTree || '', {
        paths: this.mwcMatchRouterExact ? 'exact' : 'subset',
        queryParams: this.mwcMatchRouterExact ? 'exact' : 'subset',
        fragment: 'ignored',
        matrixParams: 'ignored'
      });
  }

  constructor(
    private mwcMenuService: MenuService,
    private cdr: ChangeDetectorRef,
    @Optional() private mwcSubmenuService: MwcSubmenuService,
    @Inject(MwcIsMenuInsideDropDownToken) public isMenuInsideDropDown: boolean,
    @Optional() private directionality: Directionality,
    @Optional() private routerLink?: RouterLink,
    @Optional() private router?: Router
  ) {
    if (router) {
      this.router!.events.pipe(
        takeUntil(this.destroy$),
        filter(e => e instanceof NavigationEnd)
      ).subscribe(() => {
        this.updateRouterActive();
      });
    }
  }

  ngOnInit(): void {
    /** store origin padding in padding */
    combineLatest([this.mwcMenuService.mode$, this.mwcMenuService.inlineIndent$])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([mode, inlineIndent]) => {
        this.inlinePaddingLeft = mode === 'inline' ? this.level * inlineIndent : null;
      });

    this.dir = this.directionality.value;
    this.directionality.change?.pipe(takeUntil(this.destroy$)).subscribe((direction: Direction) => {
      this.dir = direction;
    });
  }

  ngAfterContentInit(): void {
    this.listOfRouterLink.changes.pipe(takeUntil(this.destroy$)).subscribe(() => this.updateRouterActive());
    this.updateRouterActive();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.mwcSelected) {
      this.setSelectedState(this.mwcSelected);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
